import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/AdminPanel.css';
import { clearStoredAuth, getAccessToken, getApiBaseUrl, getStoredUser } from '../utils/auth';

const STORAGE_KEYS = {
	users: 'ker.admin.users'
};

const API_PATHS = {
	createUser: '/auth/admin/users',
	listUsers: '/auth/admin/users',
	deleteUser: (id) => `/auth/admin/users/${id}`,
	usersCount: '/auth/admin/users/count',
	codes: '/auth/admin/registration-codes',
	deleteCode: (id) => `/auth/admin/registration-codes/${id}`,
	sendCode: '/auth/admin/registration-codes/send'
};

const REGIONS = [
	'Arica y Parinacota',
	'Tarapaca',
	'Antofagasta',
	'Atacama',
	'Coquimbo',
	'Valparaiso',
	'Region Metropolitana',
	"O'Higgins",
	'Maule',
	'Nuble',
	'Biobio',
	'La Araucania',
	'Los Rios',
	'Los Lagos',
	'Aysen',
	'Magallanes'
];

const EMPTY_USER_FORM = {
	tipo: '',
	nombre: '',
	apellido: '',
	rutPersonal: '',
	email: '',
	direccionParticular: '',
	ciudadParticular: '',
	regionParticular: '',
	razonSocial: '',
	rutEmpresa: '',
	direccionEmpresa: '',
	password: '',
	confirmPassword: ''
};

// helper removed: safe parsing not required after switching to server-backed users

function createLocalId(prefix) {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return `${prefix}-${crypto.randomUUID()}`;
	}

	return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function formatDate(dateValue) {
	if (!dateValue) return '-';

	const parsed = new Date(dateValue);
	if (Number.isNaN(parsed.getTime())) return '-';

	return parsed.toLocaleString('es-CL');
}

function generateCode() {
	const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
	let result = '';

	for (let index = 0; index < 16; index += 1) {
		result += chars[Math.floor(Math.random() * chars.length)];
	}

	return result;
}

function passwordIsValid(password) {
	return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(password);
}

function getBadgeLabel(tipo) {
	return tipo === 'empresa' ? 'Empresa' : 'Particular';
}

function splitFullName(value) {
	const safeValue = (value || '').toString().trim();
	if (!safeValue) {
		return { nombre: '', apellido: '' };
	}

	const parts = safeValue.split(/\s+/).filter(Boolean);
	return {
		nombre: parts[0] || '',
		apellido: parts.slice(1).join(' ')
	};
}

function isCodeExpired(dateValue) {
	if (!dateValue) return false;
	const parsed = new Date(dateValue);
	if (Number.isNaN(parsed.getTime())) return false;
	return parsed.getTime() <= Date.now();
}

function getCodeStatusLabel(status) {
	switch (status) {
		case 'expirado':
			return 'Expirado';
		case 'usado':
			return 'Usado';
		default:
			return 'Activo';
	}
}

function getCodeBadgeClass(status) {
	return status === 'activo' ? 'is-personal' : 'is-used';
}

function extractErrorMessage(payload, fallback) {
	return payload?.error || payload?.message || fallback;
}

function normalizeUser(user) {
	if (!user) return null;

	const parsedBackendId = Number(user.backendId ?? user.id ?? user.user_id);
	const backendId = Number.isInteger(parsedBackendId) && parsedBackendId > 0 ? parsedBackendId : null;
	const fullName = splitFullName(user.name);

	return {
		id: backendId ? `user-${backendId}` : user.localId || user.id || createLocalId('user'),
		backendId,
		tipo: user.tipo || (user.is_company ? 'empresa' : 'particular'),
		nombre: user.nombre || user.firstName || fullName.nombre,
		apellido: user.apellido || user.lastName || fullName.apellido,
		rutPersonal: user.rutPersonal || user.rut || '',
		correo: user.correo || user.email || '',
		direccionParticular: user.direccionParticular || user.personal_address || '',
		ciudadParticular: user.ciudadParticular || user.city || '',
		regionParticular: user.regionParticular || user.region || '',
		razonSocial: user.razonSocial || user.company || '',
		rutEmpresa: user.rutEmpresa || user.company_rut || '',
		direccionEmpresa: user.direccionEmpresa || user.company_address || '',
		fechaRegistro: user.fechaRegistro || user.createdAt || user.created_at || new Date().toISOString(),
		source: user.source || (backendId ? 'backend-admin' : 'panel-cache')
	};
}

function normalizeCode(code) {
	if (!code) return null;

	const parsedBackendId = Number(code.backendId ?? code.id);
	const backendId = Number.isInteger(parsedBackendId) && parsedBackendId > 0 ? parsedBackendId : null;
	const fechaExpiracion = code.fechaExpiracion || code.expires_at || code.expiresAt || '';
	const estado = code.estado
		|| code.status
		|| (code.usadoPor || code.used_by ? 'usado' : (code.is_expired || isCodeExpired(fechaExpiracion) ? 'expirado' : 'activo'));

	return {
		id: backendId ? `code-${backendId}` : code.localId || code.id || createLocalId('code'),
		backendId,
		codigo: (code.codigo || code.code || '').toString().trim().toUpperCase(),
		emailCliente: code.emailCliente || code.email_cliente || code.recipient_email || '',
		estado,
		usadoPor: code.usadoPor || code.usado_por || code.used_by || '',
		fechaUso: code.fechaUso || code.fecha_uso || code.used_at || '',
		fechaRegistro: code.fechaRegistro || code.created_at || code.createdAt || new Date().toISOString(),
		fechaExpiracion
	};
}

function buildUserPayload(form) {
	return {
		name: `${form.nombre.trim()} ${form.apellido.trim()}`.trim(),
		rut: form.rutPersonal.trim(),
		company_rut: form.tipo === 'empresa' ? form.rutEmpresa.trim() : '',
		email: form.email.trim(),
		password: form.password,
		is_company: form.tipo === 'empresa' ? 1 : 0,
		company: form.tipo === 'empresa' ? form.razonSocial.trim() : '',
		personal_address: form.tipo === 'particular' ? form.direccionParticular.trim() : '',
		company_address: form.tipo === 'empresa' ? form.direccionEmpresa.trim() : ''
	};
}

function buildStoredUser(form, backendId) {
	return normalizeUser({
		backendId,
		tipo: form.tipo,
		nombre: form.nombre.trim(),
		apellido: form.apellido.trim(),
		rutPersonal: form.rutPersonal.trim(),
		correo: form.email.trim(),
		direccionParticular: form.tipo === 'particular' ? form.direccionParticular.trim() : '',
		ciudadParticular: form.tipo === 'particular' ? form.ciudadParticular.trim() : '',
		regionParticular: form.tipo === 'particular' ? form.regionParticular.trim() : '',
		razonSocial: form.tipo === 'empresa' ? form.razonSocial.trim() : '',
		rutEmpresa: form.tipo === 'empresa' ? form.rutEmpresa.trim() : '',
		direccionEmpresa: form.tipo === 'empresa' ? form.direccionEmpresa.trim() : '',
		fechaRegistro: new Date().toISOString(),
		source: 'backend-admin'
	});
}

export default function AdminPanel() {
	const navigate = useNavigate();
	const currentUser = getStoredUser();
	const currentUserBackendId = Number(currentUser?.id) || null;

	const [activeTab, setActiveTab] = useState('usuarios');
	const [users, setUsers] = useState([]);
	const [codes, setCodes] = useState([]);
	const [selectedUserId, setSelectedUserId] = useState(null);
	const [selectedCodeId, setSelectedCodeId] = useState(null);
	const [usersPage, setUsersPage] = useState(1);
	const [usersPerPage, setUsersPerPage] = useState(25);
	const [usersTotalPages, setUsersTotalPages] = useState(0);
	const [usersSearch, setUsersSearch] = useState('');
	const [serverStats, setServerStats] = useState({ total: 0, empresas: 0, particulares: 0 });
	const [hasServerStats, setHasServerStats] = useState(false);

	const [userForm, setUserForm] = useState(EMPTY_USER_FORM);
	const [generatedCode, setGeneratedCode] = useState('');
	const [codeEmail, setCodeEmail] = useState('');
	const [deleteConfirmText, setDeleteConfirmText] = useState('');

	const [isUserModalOpen, setIsUserModalOpen] = useState(false);
	const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isInitializing, setIsInitializing] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [isCreatingUser, setIsCreatingUser] = useState(false);
	const [isCreatingCode, setIsCreatingCode] = useState(false);
	const [isDeletingUser, setIsDeletingUser] = useState(false);
	const [isDeletingCode, setIsDeletingCode] = useState(false);
	const [sendingCodeId, setSendingCodeId] = useState(null);

	const [userFormError, setUserFormError] = useState('');
	const [codeFormError, setCodeFormError] = useState('');
	const [deleteError, setDeleteError] = useState('');
	const [pageError, setPageError] = useState('');
	const [toasts, setToasts] = useState([]);

	useEffect(() => {
		localStorage.setItem(STORAGE_KEYS.users, JSON.stringify(users));
	}, [users]);

	useEffect(() => {
		if (selectedUserId && !users.some((user) => user.id === selectedUserId)) {
			setSelectedUserId(null);
		}
	}, [selectedUserId, users]);

	useEffect(() => {
		if (selectedCodeId && !codes.some((code) => code.id === selectedCodeId)) {
			setSelectedCodeId(null);
		}
	}, [selectedCodeId, codes]);

	const showToast = useCallback((message, type = 'info') => {
		const id = createLocalId('toast');
		setToasts((current) => [...current, { id, message, type }]);

		window.setTimeout(() => {
			setToasts((current) => current.filter((toast) => toast.id !== id));
		}, 3200);
	}, []);

	const handleAuthFailure = useCallback((status) => {
		if (status === 401) {
			clearStoredAuth();
			navigate('/login', { replace: true });
			return;
		}

		if (status === 403) {
			navigate('/dashboard', { replace: true });
		}
	}, [navigate]);

	const performAdminRequest = useCallback(async (path, options = {}) => {
		const token = getAccessToken();

		if (!token) {
			handleAuthFailure(401);
			return {
				ok: false,
				status: 401,
				data: { error: 'Tu sesion expiro. Vuelve a iniciar sesion.' }
			};
		}

		const hasJsonBody = typeof options.body === 'string';
		const headers = {
			...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
			...(options.headers || {}),
			Authorization: `Bearer ${token}`
		};

		try {
			const response = await fetch(`${getApiBaseUrl()}${path}`, {
				...options,
				headers
			});

			const data = await response.json().catch(() => ({}));

			if (response.status === 401 || response.status === 403) {
				handleAuthFailure(response.status);
			}

			return {
				ok: response.ok,
				status: response.status,
				data
			};
		} catch (error) {
			return {
				ok: false,
				status: 0,
				error,
				data: { error: 'No se pudo conectar con el servidor.' }
			};
		}
	}, [handleAuthFailure]);

	const fetchUserStats = useCallback(async () => {
		const result = await performAdminRequest(API_PATHS.usersCount, { method: 'GET' });

		if (!result.ok) {
			return { ok: false, message: extractErrorMessage(result.data, 'No se pudo cargar el conteo de usuarios.') };
		}

		const count = result.data?.count || {};
		setServerStats({
			total: Number(count.total) || 0,
			empresas: Number(count.companies) || 0,
			particulares: Number(count.individuals) || 0
		});
		setHasServerStats(true);

		return { ok: true };
	}, [performAdminRequest]);

	const fetchRegistrationCodes = useCallback(async () => {
		const result = await performAdminRequest(API_PATHS.codes, { method: 'GET' });

		if (!result.ok) {
			return { ok: false, message: extractErrorMessage(result.data, 'No se pudieron cargar los codigos.') };
		}

		const nextCodes = Array.isArray(result.data?.items)
			? result.data.items.map(normalizeCode).filter(Boolean)
			: [];

		setCodes(nextCodes);
		return { ok: true };
	}, [performAdminRequest]);

	const fetchUsers = useCallback(async ({ page = 1, perPage = 25, q = '', showErrorsAsToast = false } = {}) => {
		const params = new URLSearchParams();
		params.append('page', String(page));
		params.append('perPage', String(perPage));
		if (q) params.append('q', q);

		setIsRefreshing(true);
		const result = await performAdminRequest(`${API_PATHS.listUsers}?${params.toString()}`, { method: 'GET' });
		setIsRefreshing(false);

		if (!result.ok) {
			if (showErrorsAsToast) showToast(extractErrorMessage(result.data, 'No fue posible cargar los usuarios.'));
			return { ok: false, message: extractErrorMessage(result.data, 'No fue posible cargar los usuarios.') };
		}

		const items = Array.isArray(result.data?.items) ? result.data.items.map(normalizeUser).filter(Boolean) : [];
		setUsers(items);

		const meta = result.data?.meta || {};
		setUsersPage(Number(meta.page) || page);
		setUsersPerPage(Number(meta.perPage) || perPage);
		setUsersTotalPages(Number(meta.totalPages) || Math.ceil((Number(meta.total) || items.length) / perPage));
		// total available in meta if needed

		setHasServerStats(true);

		return { ok: true, items };
	}, [performAdminRequest, showToast]);

	const syncAdminData = useCallback(async (showErrorsAsToast = false) => {
		setPageError('');
		const [statsResult, codesResult, usersResult] = await Promise.all([
			fetchUserStats(),
			fetchRegistrationCodes(),
			fetchUsers()
		]);
		const failures = [statsResult, codesResult, usersResult].filter((result) => !result || !result.ok);

		if (failures.length > 0) {
			const message = failures.map((result) => result.message).filter(Boolean).join(' ');
			setPageError(message || 'No fue posible sincronizar el panel con el backend.');

			if (showErrorsAsToast) {
				showToast(message || 'No fue posible sincronizar el panel.', 'error');
			}

			return false;
		}

		return true;
	}, [fetchRegistrationCodes, fetchUserStats, fetchUsers, showToast]);

	useEffect(() => {
		let cancelled = false;

		async function loadInitialData() {
			await syncAdminData(false);

			if (!cancelled) {
				setIsInitializing(false);
			}
		}

		loadInitialData();

		return () => {
			cancelled = true;
		};
	}, [syncAdminData]);

	const selectedUser = useMemo(
		() => users.find((user) => user.id === selectedUserId) || null,
		[selectedUserId, users]
	);

	const selectedCode = useMemo(
		() => codes.find((code) => code.id === selectedCodeId) || null,
		[codes, selectedCodeId]
	);

	const cachedStats = useMemo(() => {
		const empresas = users.filter((user) => user.tipo === 'empresa').length;
		const particulares = users.filter((user) => user.tipo === 'particular').length;

		return {
			total: users.length,
			empresas,
			particulares
		};
	}, [users]);

	const stats = hasServerStats ? serverStats : cachedStats;
	const isBusy = isInitializing || isRefreshing || isCreatingUser || isCreatingCode || isDeletingUser || isDeletingCode || sendingCodeId !== null;

	function openUserModal() {
		setUserForm(EMPTY_USER_FORM);
		setUserFormError('');
		setIsUserModalOpen(true);
	}

	function closeUserModal() {
		setIsUserModalOpen(false);
		setUserFormError('');
		setUserForm(EMPTY_USER_FORM);
	}

	function openCodeModal() {
		setGeneratedCode(generateCode());
		setCodeEmail('');
		setCodeFormError('');
		setIsCodeModalOpen(true);
	}

	function closeCodeModal() {
		setIsCodeModalOpen(false);
		setCodeFormError('');
		setGeneratedCode('');
		setCodeEmail('');
	}

	function closeDeleteModal() {
		setDeleteConfirmText('');
		setDeleteError('');
		setIsDeleteModalOpen(false);
	}

	function handleUserFieldChange(event) {
		const { name, value } = event.target;
		setUserForm((current) => {
			const next = { ...current, [name]: value };

			if (name === 'tipo') {
				if (value === 'particular') {
					next.razonSocial = '';
					next.rutEmpresa = '';
					next.direccionEmpresa = '';
				}

				if (value === 'empresa') {
					next.direccionParticular = '';
					next.ciudadParticular = '';
					next.regionParticular = '';
				}
			}

			return next;
		});
	}

	function validateUserForm() {
		if (!userForm.tipo) return 'Selecciona el tipo de usuario.';
		if (!userForm.nombre.trim() || !userForm.apellido.trim()) return 'Completa nombre y apellido.';
		if (!userForm.rutPersonal.trim()) return 'Ingresa el RUT personal.';
		if (!userForm.email.trim()) return 'Ingresa el correo electronico.';
		if (!userForm.password) return 'Ingresa una contrasena.';
		if (userForm.password !== userForm.confirmPassword) return 'Las contrasenas no coinciden.';
		if (!passwordIsValid(userForm.password)) {
			return 'La contrasena debe tener 8 caracteres minimo, una mayuscula, una minuscula y un numero.';
		}

		if (userForm.tipo === 'particular') {
			if (!userForm.direccionParticular.trim() || !userForm.ciudadParticular.trim() || !userForm.regionParticular.trim()) {
				return 'Completa la direccion, ciudad y region del usuario particular.';
			}
		}

		if (userForm.tipo === 'empresa') {
			if (!userForm.razonSocial.trim() || !userForm.rutEmpresa.trim() || !userForm.direccionEmpresa.trim()) {
				return 'Completa razon social, RUT y direccion de la empresa.';
			}
		}

		return '';
	}

	async function handleRefreshPanel() {
		setIsRefreshing(true);
		const ok = await syncAdminData(true);
		setIsRefreshing(false);

		if (ok) {
			showToast('Panel sincronizado con el backend.', 'success');
		}
	}

	async function handleCreateUser(event) {
		event.preventDefault();
		const validationError = validateUserForm();

		if (validationError) {
			setUserFormError(validationError);
			return;
		}

		setIsCreatingUser(true);
		setUserFormError('');

		const result = await performAdminRequest(API_PATHS.createUser, {
			method: 'POST',
			body: JSON.stringify(buildUserPayload(userForm))
		});

		if (!result.ok) {
			setUserFormError(extractErrorMessage(result.data, 'No fue posible crear el usuario.'));
			setIsCreatingUser(false);
			return;
		}

		const createdId = result.data?.id || null;
		if (createdId) {
			await fetchUsers({ page: 1, perPage: usersPerPage, q: usersSearch, showErrorsAsToast: true });
			setSelectedUserId(`user-${createdId}`);
		} else {
			const createdUser = buildStoredUser(userForm, null);
			setUsers((current) => {
				const nextUsers = current.filter(
					(item) => item.backendId !== createdUser.backendId && item.correo !== createdUser.correo
				);
				return [createdUser, ...nextUsers];
			});
			setSelectedUserId(createdUser.id);
		}

		closeUserModal();
		await fetchUserStats();
		showToast(result.data?.message || 'Usuario creado por administrador.', 'success');
		setIsCreatingUser(false);
	}

	function openDeleteModal() {
		setDeleteConfirmText('');
		setDeleteError('');
		setIsDeleteModalOpen(true);
	}

	async function handleDeleteUser() {
		if (!selectedUser) return;

		if (deleteConfirmText !== 'ELIMINAR') {
			setDeleteError('Debes escribir ELIMINAR exactamente para confirmar.');
			return;
		}

		if (!selectedUser.backendId) {
			setDeleteError('Ese usuario no tiene un identificador valido del backend y no puede eliminarse desde aqui.');
			return;
		}

		if (currentUserBackendId && selectedUser.backendId === currentUserBackendId) {
			setDeleteError('No puedes eliminar tu propia cuenta desde el panel.');
			return;
		}

		setIsDeletingUser(true);
		setDeleteError('');

		const result = await performAdminRequest(API_PATHS.deleteUser(selectedUser.backendId), {
			method: 'DELETE'
		});

		if (!result.ok) {
			setDeleteError(extractErrorMessage(result.data, 'No fue posible eliminar el usuario.'));
			setIsDeletingUser(false);
			return;
		}

		await fetchUsers({ page: usersPage, perPage: usersPerPage, q: usersSearch, showErrorsAsToast: true });
		setSelectedUserId(null);
		closeDeleteModal();
		await fetchUserStats();
		showToast(result.data?.message || 'Usuario eliminado correctamente.', 'success');
		setIsDeletingUser(false);
	}

	async function handleCreateCode(event) {
		event.preventDefault();
		const nextCode = generatedCode.trim().toUpperCase();
		const recipientEmail = codeEmail.trim();

		if (!nextCode) {
			setCodeFormError('Genera un codigo antes de guardarlo.');
			return;
		}

		if (!recipientEmail) {
			setCodeFormError('Ingresa el correo del cliente.');
			return;
		}

		setIsCreatingCode(true);
		setCodeFormError('');

		const createResult = await performAdminRequest(API_PATHS.codes, {
			method: 'POST',
			body: JSON.stringify({
				code: nextCode,
				recipientEmail
			})
		});

		if (!createResult.ok) {
			setCodeFormError(extractErrorMessage(createResult.data, 'No fue posible crear el codigo.'));
			setIsCreatingCode(false);
			return;
		}

		const createdCode = normalizeCode(createResult.data?.item || {
			code: nextCode,
			recipient_email: recipientEmail,
			expires_at: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString()
		});

		setCodes((current) => {
			const nextCodes = current.filter((item) => item.backendId !== createdCode.backendId && item.codigo !== createdCode.codigo);
			return [createdCode, ...nextCodes];
		});
		setSelectedCodeId(createdCode.id);
		closeCodeModal();

		const sendResult = await performAdminRequest(API_PATHS.sendCode, {
			method: 'POST',
			body: JSON.stringify({
				code: createdCode.codigo,
				recipientEmail
			})
		});

		await fetchRegistrationCodes();

		if (sendResult.ok) {
			showToast('Codigo creado y enviado por correo correctamente.', 'success');
		} else {
			showToast(
				extractErrorMessage(sendResult.data, 'El codigo fue creado, pero no se pudo enviar por correo.'),
				'error'
			);
		}

		setIsCreatingCode(false);
	}

	async function handleCopyCode() {
		if (!selectedCode?.codigo) return;

		try {
			await navigator.clipboard.writeText(selectedCode.codigo);
			showToast('Codigo copiado al portapapeles.', 'success');
		} catch (error) {
			showToast('No se pudo copiar el codigo.', 'error');
		}
	}

	async function handleSendCodeEmail(codeItem = selectedCode) {
		if (!codeItem?.codigo || !codeItem?.emailCliente) {
			showToast('Este codigo no tiene un correo asociado para reenviar.', 'error');
			return;
		}

		setSendingCodeId(codeItem.id);
		const result = await performAdminRequest(API_PATHS.sendCode, {
			method: 'POST',
			body: JSON.stringify({
				code: codeItem.codigo,
				recipientEmail: codeItem.emailCliente
			})
		});

		if (result.ok) {
			showToast('Correo reenviado correctamente.', 'success');
			await fetchRegistrationCodes();
		} else {
			showToast(extractErrorMessage(result.data, 'No se pudo reenviar el correo.'), 'error');
		}

		setSendingCodeId(null);
	}

	async function handleDeleteCode() {
		if (!selectedCode?.backendId) {
			showToast('El codigo seleccionado no tiene un id valido del backend.', 'error');
			return;
		}

		setIsDeletingCode(true);
		const result = await performAdminRequest(API_PATHS.deleteCode(selectedCode.backendId), {
			method: 'DELETE'
		});

		if (!result.ok) {
			showToast(extractErrorMessage(result.data, 'No fue posible eliminar el codigo.'), 'error');
			setIsDeletingCode(false);
			return;
		}

		setCodes((current) => current.filter((code) => code.id !== selectedCode.id));
		setSelectedCodeId(null);
		showToast(result.data?.message || 'Codigo eliminado.', 'success');
		setIsDeletingCode(false);
	}

	function handleLogout() {
		clearStoredAuth();
		navigate('/login', { replace: true });
	}

	return (
		<div className="admin-panel-page">
			<div className="admin-panel-shell">
				<header className="admin-panel-header">
					<div className="admin-panel-brand">
						<div className="admin-panel-brand-mark">KER</div>
						<div>
							<h1>Panel de Administracion</h1>
							<p>Gestiona usuarios y codigos de registro desde un solo lugar.</p>
						</div>
					</div>

					<div className="admin-panel-header-actions">
						<button type="button" className="admin-panel-secondary-button" onClick={handleRefreshPanel}>
							Sincronizar
						</button>
						<button type="button" className="admin-panel-secondary-button" onClick={() => navigate('/dashboard')}>
							Volver al dashboard
						</button>
						<button type="button" className="admin-panel-secondary-button" onClick={handleLogout}>
							Cerrar sesion
						</button>
						<button type="button" className="admin-panel-primary-button" onClick={openUserModal}>
							+ Nuevo Usuario
						</button>
					</div>
				</header>

				<section className="admin-panel-toolbar">
					<div>
						<div className="admin-panel-eyebrow">Administrador activo</div>
						<div className="admin-panel-admin-name">
							{currentUser?.name || currentUser?.nombre || 'Administrador'}
						</div>
					</div>

					<div className="admin-panel-tabs" role="tablist" aria-label="Secciones del panel">
						<button
							type="button"
							className={`admin-panel-tab ${activeTab === 'usuarios' ? 'is-active' : ''}`}
							onClick={() => setActiveTab('usuarios')}
						>
							Usuarios
							<span>{stats.total}</span>
						</button>
						<button
							type="button"
							className={`admin-panel-tab ${activeTab === 'codigos' ? 'is-active' : ''}`}
							onClick={() => setActiveTab('codigos')}
						>
							Codigos de Registro
							<span>{codes.length}</span>
						</button>
					</div>
				</section>

				{pageError && <div className="admin-panel-inline-error">{pageError}</div>}

				{activeTab === 'usuarios' ? (
					<section className="admin-panel-grid">
						<aside className="admin-panel-column admin-panel-column-list">
							<div className="admin-panel-card admin-panel-list-card">
								<div className="admin-panel-card-header">
									<div>
										<h2>Usuarios registrados</h2>
										<p>
											{hasServerStats
												? 'Lista cargada desde el backend. Usa búsqueda y paginación para filtrar.'
												: 'Mostrando usuarios creados recientemente desde este panel.'}
										</p>
									</div>
									<span className="admin-panel-count-chip">{stats.total}</span>
								</div>

								<div className="admin-panel-card-controls">
									<div className="admin-panel-search">
										<input
											type="search"
											placeholder="Buscar por nombre, correo, RUT o empresa"
											value={usersSearch}
											onChange={(e) => setUsersSearch(e.target.value)}
										/>
										<button
											type="button"
											className="admin-panel-primary-button is-small"
											onClick={() => fetchUsers({ page: 1, perPage: usersPerPage, q: usersSearch, showErrorsAsToast: true })}
										>
											Buscar
										</button>
										<button
											type="button"
											className="admin-panel-secondary-button is-small"
											onClick={() => { setUsersSearch(''); fetchUsers({ page: 1, perPage: usersPerPage, q: '', showErrorsAsToast: true }); }}
										>
											Limpiar
										</button>
									</div>

									<div className="admin-panel-pagination">
										<button
											type="button"
											className="admin-panel-secondary-button is-small"
											onClick={() => fetchUsers({ page: Math.max(1, usersPage - 1), perPage: usersPerPage, q: usersSearch })}
											disabled={usersPage <= 1}
										>
											Anterior
										</button>
										<span className="admin-panel-pagination-info">Página {usersPage} de {usersTotalPages || 1}</span>
										<button
											type="button"
											className="admin-panel-secondary-button is-small"
											onClick={() => fetchUsers({ page: Math.min(usersTotalPages || usersPage + 1, usersPage + 1), perPage: usersPerPage, q: usersSearch })}
											disabled={usersPage >= (usersTotalPages || 1)}
										>
											Siguiente
										</button>
									</div>
								</div>

								<div className="admin-panel-list scrollbar-custom">
									{users.length === 0 ? (
										<div className="admin-panel-empty-state">
											<div className="admin-panel-empty-icon">U</div>
											<h3>No hay usuarios recientes en el panel</h3>
											<p>Crea un usuario para dejarlo disponible aqui y actualizar el conteo real del backend.</p>
										</div>
									) : (
										users.map((user) => (
											<button
												key={user.id}
												type="button"
												className={`admin-panel-list-item ${selectedUserId === user.id ? 'is-active' : ''}`}
												onClick={() => setSelectedUserId(user.id)}
											>
												<div className="admin-panel-avatar">{(user.nombre?.[0] || 'U').toUpperCase()}</div>
												<div className="admin-panel-list-copy">
													<strong>{`${user.nombre} ${user.apellido}`.trim()}</strong>
													<span>{user.correo || 'Sin correo'}</span>
													<em className={`admin-panel-user-badge ${user.tipo === 'empresa' ? 'is-company' : 'is-personal'}`}>
														{getBadgeLabel(user.tipo)}
													</em>
												</div>
												<div className="admin-panel-list-arrow">&gt;</div>
											</button>
										))
									)}
								</div>
							</div>
						</aside>

						<div className="admin-panel-column admin-panel-column-detail">
							{!selectedUser ? (
								<div className="admin-panel-card admin-panel-welcome-card">
									<div className="admin-panel-welcome-icon">+</div>
									<h2>Bienvenido al panel</h2>
									<p>Desde aqui puedes crear usuarios sin requerir codigo Ker y sincronizar el conteo con el backend.</p>

									<div className="admin-panel-stat-grid">
										<article className="admin-panel-stat-card">
											<strong>{stats.total}</strong>
											<span>Total usuarios</span>
										</article>
										<article className="admin-panel-stat-card">
											<strong>{stats.empresas}</strong>
											<span>Empresas</span>
										</article>
										<article className="admin-panel-stat-card">
											<strong>{stats.particulares}</strong>
											<span>Particulares</span>
										</article>
										<article className="admin-panel-stat-card">
											<strong>API</strong>
											<span>Sincronizada</span>
										</article>
									</div>
								</div>
							) : (
								<div className="admin-panel-card admin-panel-detail-card">
									<div className="admin-panel-detail-hero">
										<div className="admin-panel-detail-profile">
											<div className="admin-panel-detail-avatar">{(selectedUser.nombre?.[0] || 'U').toUpperCase()}</div>
											<div>
												<h2>{`${selectedUser.nombre} ${selectedUser.apellido}`.trim()}</h2>
												<span className={`admin-panel-user-badge ${selectedUser.tipo === 'empresa' ? 'is-company' : 'is-personal'}`}>
													{getBadgeLabel(selectedUser.tipo)}
												</span>
											</div>
										</div>

										<button type="button" className="admin-panel-close-button" onClick={() => setSelectedUserId(null)}>
											x
										</button>
									</div>

									<div className="admin-panel-detail-body scrollbar-custom">
										<section className="admin-panel-detail-section">
											<h3>Datos personales</h3>
											<div className="admin-panel-info-grid">
												<div>
													<label>Nombre</label>
													<p>{selectedUser.nombre || '-'}</p>
												</div>
												<div>
													<label>Apellido</label>
													<p>{selectedUser.apellido || '-'}</p>
												</div>
												<div>
													<label>RUT personal</label>
													<p>{selectedUser.rutPersonal || '-'}</p>
												</div>
												<div>
													<label>Correo electronico</label>
													<p>{selectedUser.correo || '-'}</p>
												</div>
											</div>
										</section>

										{selectedUser.tipo === 'empresa' ? (
											<section className="admin-panel-detail-section">
												<h3>Datos de la empresa</h3>
												<div className="admin-panel-info-grid">
													<div>
														<label>Razon social</label>
														<p>{selectedUser.razonSocial || '-'}</p>
													</div>
													<div>
														<label>RUT empresa</label>
														<p>{selectedUser.rutEmpresa || '-'}</p>
													</div>
													<div className="admin-panel-span-2">
														<label>Direccion empresa</label>
														<p>{selectedUser.direccionEmpresa || '-'}</p>
													</div>
												</div>
											</section>
										) : (
											<section className="admin-panel-detail-section">
												<h3>Direccion particular</h3>
												<div className="admin-panel-info-grid">
													<div className="admin-panel-span-2">
														<label>Direccion</label>
														<p>{selectedUser.direccionParticular || '-'}</p>
													</div>
													<div>
														<label>Ciudad</label>
														<p>{selectedUser.ciudadParticular || '-'}</p>
													</div>
													<div>
														<label>Region</label>
														<p>{selectedUser.regionParticular || '-'}</p>
													</div>
												</div>
											</section>
										)}

										<section className="admin-panel-detail-section">
											<h3>Registro</h3>
											<div className="admin-panel-info-grid">
												<div>
													<label>Fecha de registro</label>
													<p>{formatDate(selectedUser.fechaRegistro)}</p>
												</div>
												<div>
													<label>Origen</label>
													<p>{selectedUser.source === 'backend-admin' ? 'Creado desde panel admin' : 'Cache local del panel'}</p>
												</div>
											</div>
										</section>
									</div>

									<div className="admin-panel-detail-actions">
										<button type="button" className="admin-panel-secondary-button is-blue" onClick={handleRefreshPanel}>
											Sincronizar panel
										</button>
										<button type="button" className="admin-panel-danger-button" onClick={openDeleteModal}>
											Eliminar usuario
										</button>
									</div>
								</div>
							)}
						</div>
					</section>
				) : (
					<section className="admin-panel-grid">
						<aside className="admin-panel-column admin-panel-column-list">
							<div className="admin-panel-card admin-panel-list-card">
								<div className="admin-panel-card-header">
									<div>
										<h2>Codigos generados</h2>
										<p>Estos codigos se cargan desde el backend y pueden reenviarse por correo.</p>
									</div>
									<button type="button" className="admin-panel-primary-button is-small" onClick={openCodeModal}>
										+ Nuevo Codigo
									</button>
								</div>

								<div className="admin-panel-list scrollbar-custom">
									{codes.length === 0 ? (
										<div className="admin-panel-empty-state">
											<div className="admin-panel-empty-icon">#</div>
											<h3>No hay codigos creados</h3>
											<p>Genera un codigo de registro y el panel intentara enviarlo por correo al cliente.</p>
										</div>
									) : (
										codes.map((code) => (
											<button
												key={code.id}
												type="button"
												className={`admin-panel-list-item ${selectedCodeId === code.id ? 'is-active' : ''}`}
												onClick={() => setSelectedCodeId(code.id)}
											>
												<div className="admin-panel-avatar is-code">#</div>
												<div className="admin-panel-list-copy">
													<strong className="admin-panel-mono">{code.codigo}</strong>
													<span>{code.emailCliente || 'Sin correo'}</span>
													<em className={`admin-panel-user-badge ${getCodeBadgeClass(code.estado)}`}>
														{getCodeStatusLabel(code.estado)}
													</em>
												</div>
												<div className="admin-panel-list-arrow">&gt;</div>
											</button>
										))
									)}
								</div>
							</div>
						</aside>

						<div className="admin-panel-column admin-panel-column-detail">
							{!selectedCode ? (
								<div className="admin-panel-card admin-panel-welcome-card">
									<div className="admin-panel-welcome-icon">#</div>
									<h2>Control de acceso</h2>
									<p>Genera codigos temporales, envialos por correo y elimina los que ya no necesites.</p>

									<div className="admin-panel-steps">
										<div><span>1</span>Genera un codigo aleatorio o personalizado.</div>
										<div><span>2</span>Asocialo al correo del cliente.</div>
										<div><span>3</span>El panel crea el codigo en el backend y envia el email.</div>
										<div><span>4</span>Reenvialo o eliminalo cuando sea necesario.</div>
									</div>
								</div>
							) : (
								<div className="admin-panel-card admin-panel-detail-card">
									<div className="admin-panel-detail-hero">
										<div>
											<h2>Codigo de registro</h2>
											<span className={`admin-panel-user-badge ${getCodeBadgeClass(selectedCode.estado)}`}>
												{getCodeStatusLabel(selectedCode.estado)}
											</span>
										</div>

										<button type="button" className="admin-panel-close-button" onClick={() => setSelectedCodeId(null)}>
											x
										</button>
									</div>

									<div className="admin-panel-detail-body">
										<section className="admin-panel-detail-section">
											<h3>Codigo de invitacion</h3>
											<div className="admin-panel-code-box">
												<code>{selectedCode.codigo}</code>
												<button type="button" className="admin-panel-primary-button is-small" onClick={handleCopyCode}>
													Copiar
												</button>
											</div>
										</section>

										<section className="admin-panel-detail-section">
											<h3>Informacion del cliente</h3>
											<div className="admin-panel-info-grid">
												<div>
													<label>Correo electronico</label>
													<p>{selectedCode.emailCliente || '-'}</p>
												</div>
												<div>
													<label>Fecha de creacion</label>
													<p>{formatDate(selectedCode.fechaRegistro)}</p>
												</div>
												<div>
													<label>Fecha de expiracion</label>
													<p>{formatDate(selectedCode.fechaExpiracion)}</p>
												</div>
												<div>
													<label>Estado</label>
													<p>{getCodeStatusLabel(selectedCode.estado)}</p>
												</div>
											</div>
										</section>
									</div>

									<div className="admin-panel-detail-actions">
										<button
											type="button"
											className="admin-panel-secondary-button is-blue"
											onClick={() => handleSendCodeEmail(selectedCode)}
											disabled={sendingCodeId === selectedCode.id || !selectedCode.emailCliente}
										>
											{sendingCodeId === selectedCode.id ? 'Enviando...' : 'Reenviar correo'}
										</button>
										<button type="button" className="admin-panel-danger-button" onClick={handleDeleteCode}>
											Eliminar codigo
										</button>
									</div>
								</div>
							)}
						</div>
					</section>
				)}

				{isUserModalOpen && (
					<div className="admin-panel-overlay">
						<div className="admin-panel-modal admin-panel-modal-wide">
							<div className="admin-panel-modal-header">
								<div>
									<h3>Nuevo Usuario</h3>
									<p>Completa los datos del registro sin pedir codigo Ker.</p>
								</div>
								<button type="button" className="admin-panel-close-button" onClick={closeUserModal}>
									x
								</button>
							</div>

							<form className="admin-panel-form scrollbar-custom" onSubmit={handleCreateUser}>
								<section className="admin-panel-form-section">
									<div className="admin-panel-form-title">Tipo de usuario</div>
									<div className="admin-panel-select-grid">
										<label className="admin-panel-input-block admin-panel-span-2">
											<span>Tipo *</span>
											<select name="tipo" value={userForm.tipo} onChange={handleUserFieldChange} required>
												<option value="">Seleccionar tipo</option>
												<option value="particular">Usuario Particular</option>
												<option value="empresa">Empresa</option>
											</select>
										</label>
									</div>
								</section>

								<section className="admin-panel-form-section">
									<div className="admin-panel-form-title">Datos personales</div>
									<div className="admin-panel-form-grid">
										<label className="admin-panel-input-block">
											<span>Nombre *</span>
											<input name="nombre" value={userForm.nombre} onChange={handleUserFieldChange} placeholder="Nombre" required />
										</label>
										<label className="admin-panel-input-block">
											<span>Apellido *</span>
											<input name="apellido" value={userForm.apellido} onChange={handleUserFieldChange} placeholder="Apellido" required />
										</label>
										<label className="admin-panel-input-block">
											<span>RUT Personal *</span>
											<input name="rutPersonal" value={userForm.rutPersonal} onChange={handleUserFieldChange} placeholder="12.345.678-9" required />
										</label>
										<label className="admin-panel-input-block">
											<span>Correo Electronico *</span>
											<input name="email" type="email" value={userForm.email} onChange={handleUserFieldChange} placeholder="correo@ejemplo.com" required />
										</label>
									</div>
								</section>

								{userForm.tipo === 'particular' && (
									<section className="admin-panel-form-section">
										<div className="admin-panel-form-title">Direccion personal</div>
										<div className="admin-panel-form-grid">
											<label className="admin-panel-input-block admin-panel-span-2">
												<span>Direccion *</span>
												<input name="direccionParticular" value={userForm.direccionParticular} onChange={handleUserFieldChange} placeholder="Calle, numero, depto o casa" required />
											</label>
											<label className="admin-panel-input-block">
												<span>Ciudad *</span>
												<input name="ciudadParticular" value={userForm.ciudadParticular} onChange={handleUserFieldChange} placeholder="Ciudad" required />
											</label>
											<label className="admin-panel-input-block">
												<span>Region *</span>
												<select name="regionParticular" value={userForm.regionParticular} onChange={handleUserFieldChange} required>
													<option value="">Selecciona una region</option>
													{REGIONS.map((region) => (
														<option key={region} value={region}>{region}</option>
													))}
												</select>
											</label>
										</div>
									</section>
								)}

								{userForm.tipo === 'empresa' && (
									<section className="admin-panel-form-section">
										<div className="admin-panel-form-title">Datos de la empresa</div>
										<div className="admin-panel-form-grid">
											<label className="admin-panel-input-block">
												<span>Razon Social *</span>
												<input name="razonSocial" value={userForm.razonSocial} onChange={handleUserFieldChange} placeholder="Nombre legal de la empresa" required />
											</label>
											<label className="admin-panel-input-block">
												<span>RUT Empresa *</span>
												<input name="rutEmpresa" value={userForm.rutEmpresa} onChange={handleUserFieldChange} placeholder="76.123.456-7" required />
											</label>
											<label className="admin-panel-input-block admin-panel-span-2">
												<span>Direccion de la empresa *</span>
												<input name="direccionEmpresa" value={userForm.direccionEmpresa} onChange={handleUserFieldChange} placeholder="Calle, numero, oficina" required />
											</label>
										</div>
									</section>
								)}

								<section className="admin-panel-form-section">
									<div className="admin-panel-form-title">Seguridad</div>
									<div className="admin-panel-form-grid">
										<label className="admin-panel-input-block">
											<span>Contrasena *</span>
											<input name="password" type="password" value={userForm.password} onChange={handleUserFieldChange} placeholder="Minimo 8 caracteres" required />
										</label>
										<label className="admin-panel-input-block">
											<span>Confirmar contrasena *</span>
											<input name="confirmPassword" type="password" value={userForm.confirmPassword} onChange={handleUserFieldChange} placeholder="Repite la contrasena" required />
										</label>
									</div>
									<div className="admin-panel-password-rules">
										Debe incluir 8 caracteres minimo, una mayuscula, una minuscula y un numero.
									</div>
								</section>

								{userFormError && <div className="admin-panel-inline-error">{userFormError}</div>}

								<div className="admin-panel-form-actions">
									<button type="button" className="admin-panel-secondary-button" onClick={closeUserModal}>
										Cancelar
									</button>
									<button type="submit" className="admin-panel-primary-button" disabled={isCreatingUser}>
										{isCreatingUser ? 'Creando...' : 'Guardar Usuario'}
									</button>
								</div>
							</form>
						</div>
					</div>
				)}

				{isCodeModalOpen && (
					<div className="admin-panel-overlay">
						<div className="admin-panel-modal">
							<div className="admin-panel-modal-header">
								<div>
									<h3>Nuevo Codigo de Registro</h3>
									<p>Genera un codigo aleatorio de 16 caracteres y envialo al cliente.</p>
								</div>
								<button type="button" className="admin-panel-close-button" onClick={closeCodeModal}>
									x
								</button>
							</div>

							<form className="admin-panel-form" onSubmit={handleCreateCode}>
								<label className="admin-panel-input-block">
									<span>Codigo *</span>
									<div className="admin-panel-code-generate-row">
										<input value={generatedCode} readOnly placeholder="Genera un codigo" />
										<button type="button" className="admin-panel-primary-button is-small" onClick={() => setGeneratedCode(generateCode())}>
											Generar
										</button>
									</div>
								</label>

								<label className="admin-panel-input-block">
									<span>Correo del cliente *</span>
									<input type="email" value={codeEmail} onChange={(event) => setCodeEmail(event.target.value)} placeholder="cliente@ejemplo.com" required />
								</label>

								{codeFormError && <div className="admin-panel-inline-error">{codeFormError}</div>}

								<div className="admin-panel-form-actions">
									<button type="button" className="admin-panel-secondary-button" onClick={closeCodeModal}>
										Cancelar
									</button>
									<button type="submit" className="admin-panel-primary-button" disabled={isCreatingCode}>
										{isCreatingCode ? 'Creando...' : 'Guardar Codigo'}
									</button>
								</div>
							</form>
						</div>
					</div>
				)}

				{isDeleteModalOpen && (
					<div className="admin-panel-overlay">
						<div className="admin-panel-modal admin-panel-modal-compact">
							<div className="admin-panel-modal-header admin-panel-modal-header-danger">
								<div>
									<h3>Confirmar eliminacion</h3>
									<p>Esta accion no se puede deshacer.</p>
								</div>
							</div>

							<div className="admin-panel-form">
								<p className="admin-panel-modal-copy">Escribe ELIMINAR para confirmar la eliminacion del usuario seleccionado.</p>
								<label className="admin-panel-input-block">
									<span>Confirmacion</span>
									<input value={deleteConfirmText} onChange={(event) => setDeleteConfirmText(event.target.value)} placeholder="ELIMINAR" />
								</label>

								{deleteError && <div className="admin-panel-inline-error">{deleteError}</div>}

								<div className="admin-panel-form-actions">
									<button type="button" className="admin-panel-secondary-button" onClick={closeDeleteModal}>
										Cancelar
									</button>
									<button type="button" className="admin-panel-danger-button" onClick={handleDeleteUser}>
										{isDeletingUser ? 'Eliminando...' : 'Eliminar Usuario'}
									</button>
								</div>
							</div>
						</div>
					</div>
				)}

				{isBusy && (
					<div className="admin-panel-overlay">
						<div className="admin-panel-loading-box">
							<div className="admin-panel-loading-spinner" />
							<p>{isInitializing ? 'Cargando panel...' : 'Procesando...'}</p>
						</div>
					</div>
				)}

				<div className="admin-panel-toast-stack">
					{toasts.map((toast) => (
						<div key={toast.id} className={`admin-panel-toast is-${toast.type}`}>
							{toast.message}
						</div>
					))}
				</div>
			</div>
		</div>
	);
}